/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

interface GoogleMapsLoadContext {
  requestLoad: () => void;
  isLoaded: boolean;
}

const MapsLoadContext = createContext<GoogleMapsLoadContext>({
  requestLoad: () => {},
  isLoaded: false,
});

/**
 * Call this at the top of any component that uses useMap(), useMapsLibrary(),
 * AdvancedMarker, etc. Triggers lazy-loading of the Google Maps JS API.
 */
export function useRequestGoogleMaps() {
  const { requestLoad, isLoaded } = useContext(MapsLoadContext);
  useEffect(() => {
    requestLoad();
  }, [requestLoad]);
  return { isLoaded };
}

interface GoogleMapsProviderProps {
  children: ReactNode;
}

/**
 * Lazy Google Maps provider. Renders children immediately without loading
 * the Maps JS API. The API only loads when a child calls useRequestGoogleMaps().
 */
export function GoogleMapsProvider({ children }: GoogleMapsProviderProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const [shouldLoad, setShouldLoad] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [APIProviderComponent, setAPIProviderComponent] = useState<React.ComponentType<any> | null>(null);

  const requestLoad = useCallback(() => {
    setShouldLoad(true);
  }, []);

  useEffect(() => {
    if (!shouldLoad || !apiKey || APIProviderComponent) return;

    import("@vis.gl/react-google-maps").then(({ APIProvider }) => {
      setAPIProviderComponent(() => APIProvider);
    });
  }, [shouldLoad, apiKey, APIProviderComponent]);

  const contextValue: GoogleMapsLoadContext = {
    requestLoad,
    isLoaded: !!APIProviderComponent,
  };

  if (!apiKey || !APIProviderComponent) {
    return (
      <MapsLoadContext.Provider value={contextValue}>
        {children}
      </MapsLoadContext.Provider>
    );
  }

  return (
    <MapsLoadContext.Provider value={contextValue}>
      <APIProviderComponent apiKey={apiKey} libraries={["places", "marker"]}>
        {children}
      </APIProviderComponent>
    </MapsLoadContext.Provider>
  );
}
